import { axiosBaseQuery } from '@client';
import { DaAssistantCapabilitiesResponse } from '@module/da-assistant/model/DaAssistantCapabilitiesResponse';
import { DaAssistantEvaluationStatusResponse } from '@module/da-assistant/model/DaAssistantEvaluationStatusResponse';
import { DaAssistantActionType } from '@module/da-assistant/model/DaAssistantActionType';
import { ApproveExpensesResponse } from '@module/da-assistant/model/ApproveExpensesResponse';
import { CaAllocateResponse } from '@module/da-assistant/model/CaAllocateResponse';
import { ParameterFixResponse } from '@module/da-assistant/model/ParameterFixResponse';
import type { HistoryLogEntryDto } from '@module/da-assistant/model/HistoryLogEntry';
import type { RevertActionResponse } from '@module/da-assistant/model/RevertActionResponse';
import type {
  DexFeedbackReason,
  DexFeedbackVote,
  DexOverallFeedbackReason
} from '@module/da-assistant/model/DexFeedback';
import { createApi } from '@reduxjs/toolkit/dist/query/react';

type DaAssistantActionWithTypedResponse =
  | DaAssistantActionType.PARAMETER_FIX
  | DaAssistantActionType.RUN_AI_CHECK
  | DaAssistantActionType.APPROVE_EXPENSES
  | DaAssistantActionType.CA_ALLOCATE
  | DaAssistantActionType.RAISE_WITH_AGENT
  | DaAssistantActionType.LOCATE_EXPENSES
  | DaAssistantActionType.OPERATIONS_SUMMARY;

export type TriggerDaAssistantActionArgs = {
  evaluationId: number;
  actionType: Exclude<
    DaAssistantActionType,
    DaAssistantActionWithTypedResponse
  >;
};

export type TriggerParameterFixArgs = {
  evaluationId: number;
};

export type TriggerRunAiCheckArgs = {
  evaluationId: number;
};

export type TriggerApproveExpensesArgs = {
  evaluationId: number;
};

export type TriggerCaAllocateArgs = {
  evaluationId: number;
};

export type TriggerRaiseWithAgentArgs = {
  evaluationId: number;
  emailBodyHtml: string;
};

export type TriggerLocateExpensesActionArgs = {
  evaluationId: number;
};

export type TriggerAsyncActionResponse = {
  logId: number;
};

export type TriggerOperationsSummaryArgs = {
  evaluationId: number;
};

export type RevertActionArgs = {
  evaluationId: number;
  /** Log of the SUCCESS action to undo. */
  logId: number;
};

export type SubmitActionFeedbackArgs = {
  evaluationId: number;
  logId: number;
  vote: DexFeedbackVote;
  reason?: DexFeedbackReason;
};

export type SubmitOverallFeedbackArgs = {
  evaluationId: number;
  vote: DexFeedbackVote;
  reason?: DexOverallFeedbackReason;
};

export type GetDaAssistantCapabilitiesArgs = {
  evaluationId: number;
};

export type GetDaAssistantEvaluationStatusArgs = {
  evaluationId: number;
};

export type GetHistoryLogArgs = {
  evaluationId: number;
};

export type GetFullHistoryLogArgs = {
  proformaId: number;
};

type DaAssistantEvaluationTag =
  | { type: 'DaAssistantCapabilities'; id: number }
  | { type: 'DaAssistantHistoryLog'; id: number };

function daAssistantEvaluationTags(
  evaluationId: number
): DaAssistantEvaluationTag[] {
  return [
    { type: 'DaAssistantCapabilities', id: evaluationId },
    { type: 'DaAssistantHistoryLog', id: evaluationId }
  ];
}

function daAssistantActionUrl(
  evaluationId: number,
  actionType: DaAssistantActionType
) {
  return `/secured/da-assistant/evaluation/${evaluationId}/actions/${actionType}`;
}

export const daAssistantApi = createApi({
  baseQuery: axiosBaseQuery(),
  reducerPath: 'da-assistant-api',
  tagTypes: ['DaAssistantCapabilities', 'DaAssistantHistoryLog'],
  endpoints: (build) => ({
    getCapabilities: build.query<
      DaAssistantCapabilitiesResponse,
      GetDaAssistantCapabilitiesArgs
    >({
      query: ({ evaluationId }) => ({
        url: `/secured/da-assistant/evaluation/${evaluationId}/capabilities`,
        method: 'GET'
      }),
      providesTags: (_res, _error, { evaluationId }) => [
        { type: 'DaAssistantCapabilities', id: evaluationId }
      ]
    }),
    getHistoryLog: build.query<HistoryLogEntryDto[], GetHistoryLogArgs>({
      query: ({ evaluationId }) => ({
        url: `/secured/da-assistant/evaluation/${evaluationId}/history-log`,
        method: 'GET'
      }),
      providesTags: (_res, _error, { evaluationId }) => [
        { type: 'DaAssistantHistoryLog', id: evaluationId }
      ]
    }),
    /** Support-only: full DEX action log for a form, across every user. */
    getFullHistoryLog: build.query<HistoryLogEntryDto[], GetFullHistoryLogArgs>(
      {
        query: ({ proformaId }) => ({
          url: `/secured/da-assistant/support/${proformaId}/dex-log`,
          method: 'GET'
        })
      }
    ),
    /**
     * Lightweight evaluation-level polling endpoint - no policy evaluation, just a
     * current-state read plus a Redis pending-flag check. This, not getCapabilities,
     * is what every DEX flow (auto-run and manual) polls to observe progress.
     */
    getEvaluationStatus: build.query<
      DaAssistantEvaluationStatusResponse,
      GetDaAssistantEvaluationStatusArgs
    >({
      query: ({ evaluationId }) => ({
        url: `/secured/da-assistant/evaluation/${evaluationId}/status`,
        method: 'GET'
      })
    }),
    // Actions with a response body get their own endpoint.
    triggerDaAssistantAction: build.mutation<
      void,
      TriggerDaAssistantActionArgs
    >({
      query: ({ evaluationId, actionType }) => ({
        url: daAssistantActionUrl(evaluationId, actionType),
        method: 'POST'
      }),
      invalidatesTags: (_res, _error, { evaluationId }) =>
        daAssistantEvaluationTags(evaluationId)
    }),
    triggerParameterFix: build.mutation<
      ParameterFixResponse,
      TriggerParameterFixArgs
    >({
      query: ({ evaluationId }) => ({
        url: daAssistantActionUrl(
          evaluationId,
          DaAssistantActionType.PARAMETER_FIX
        ),
        method: 'POST'
      }),
      invalidatesTags: (_res, _error, { evaluationId }) =>
        daAssistantEvaluationTags(evaluationId)
    }),
    triggerApproveExpenses: build.mutation<
      ApproveExpensesResponse,
      TriggerApproveExpensesArgs
    >({
      query: ({ evaluationId }) => ({
        url: daAssistantActionUrl(
          evaluationId,
          DaAssistantActionType.APPROVE_EXPENSES
        ),
        method: 'POST'
      }),
      invalidatesTags: (_res, _error, { evaluationId }) =>
        daAssistantEvaluationTags(evaluationId)
    }),
    triggerCaAllocate: build.mutation<
      CaAllocateResponse,
      TriggerCaAllocateArgs
    >({
      query: ({ evaluationId }) => ({
        url: daAssistantActionUrl(
          evaluationId,
          DaAssistantActionType.CA_ALLOCATE
        ),
        method: 'POST'
      }),
      invalidatesTags: (_res, _error, { evaluationId }) =>
        daAssistantEvaluationTags(evaluationId)
    }),
    triggerRaiseWithAgent: build.mutation<void, TriggerRaiseWithAgentArgs>({
      query: ({ evaluationId, emailBodyHtml }) => ({
        url: daAssistantActionUrl(
          evaluationId,
          DaAssistantActionType.RAISE_WITH_AGENT
        ),
        method: 'POST',
        data: {
          actionType: DaAssistantActionType.RAISE_WITH_AGENT,
          emailBodyHtml
        }
      }),
      invalidatesTags: (_res, _error, { evaluationId }) =>
        daAssistantEvaluationTags(evaluationId)
    }),
    /**
     * Kicks off RUN_AI_CHECK; completion is driven by the backend scheduler and observed
     * via daAssistantCurrentState (see DexDrawer's currentState reconciliation effect).
     */
    triggerRunAiCheck: build.mutation<
      TriggerAsyncActionResponse,
      TriggerRunAiCheckArgs
    >({
      query: ({ evaluationId }) => ({
        url: daAssistantActionUrl(
          evaluationId,
          DaAssistantActionType.RUN_AI_CHECK
        ),
        method: 'POST'
      })
    }),
    /**
     * Kicks off LOCATE_EXPENSES; completion is driven by the backend scheduler and observed
     * via daAssistantCurrentState (see DexDrawer's currentState reconciliation effect).
     */
    triggerLocateExpensesAction: build.mutation<
      TriggerAsyncActionResponse,
      TriggerLocateExpensesActionArgs
    >({
      query: ({ evaluationId }) => ({
        url: daAssistantActionUrl(
          evaluationId,
          DaAssistantActionType.LOCATE_EXPENSES
        ),
        method: 'POST'
      })
    }),
    /**
     * Kicks off OPERATIONS_SUMMARY; completion is driven by the backend scheduler and observed
     * via daAssistantCurrentState (see DexDrawer's currentState reconciliation effect).
     */
    triggerOperationsSummary: build.mutation<
      TriggerAsyncActionResponse,
      TriggerOperationsSummaryArgs
    >({
      query: ({ evaluationId }) => ({
        url: daAssistantActionUrl(
          evaluationId,
          DaAssistantActionType.OPERATIONS_SUMMARY
        ),
        method: 'POST'
      })
    }),
    revertAction: build.mutation<RevertActionResponse, RevertActionArgs>({
      query: ({ evaluationId, logId }) => ({
        url: `/secured/da-assistant/evaluation/${evaluationId}/revert-action/${logId}`,
        method: 'POST'
      }),
      invalidatesTags: (_res, _error, { evaluationId }) =>
        daAssistantEvaluationTags(evaluationId)
    }),
    submitOverallFeedback: build.mutation<void, SubmitOverallFeedbackArgs>({
      query: ({ evaluationId, vote, reason }) => ({
        url: `/secured/da-assistant/evaluation/${evaluationId}/feedback`,
        method: 'POST',
        data: { vote, reason }
      }),
      // /status stops polling once idle, so patch the vote into its cache rather than refetch.
      async onQueryStarted(
        { evaluationId, vote, reason },
        { dispatch, queryFulfilled }
      ) {
        const patch = dispatch(
          daAssistantApi.util.updateQueryData(
            'getEvaluationStatus',
            { evaluationId },
            (draft) => {
              draft.myOverallVote = vote;
              draft.myOverallReason = reason ?? null;
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      }
    }),
    submitActionFeedback: build.mutation<void, SubmitActionFeedbackArgs>({
      query: ({ evaluationId, logId, vote, reason }) => ({
        url: `/secured/da-assistant/evaluation/${evaluationId}/logs/${logId}/feedback`,
        method: 'POST',
        data: { vote, reason }
      }),
      // /status stops polling once idle, so patch the vote into its cache rather than refetch.
      async onQueryStarted(
        { evaluationId, logId, vote },
        { dispatch, queryFulfilled }
      ) {
        const patch = dispatch(
          daAssistantApi.util.updateQueryData(
            'getEvaluationStatus',
            { evaluationId },
            (draft) => {
              draft.daAssistantCurrentState
                .filter((row) => row.logId === logId)
                .forEach((row) => {
                  row.myVote = vote;
                });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_res, _error, { evaluationId }) => [
        { type: 'DaAssistantHistoryLog', id: evaluationId }
      ]
    })
  })
});

export const {
  useGetCapabilitiesQuery,
  useGetHistoryLogQuery,
  useGetFullHistoryLogQuery,
  useGetEvaluationStatusQuery,
  useTriggerDaAssistantActionMutation,
  useTriggerParameterFixMutation,
  useTriggerApproveExpensesMutation,
  useTriggerCaAllocateMutation,
  useTriggerRaiseWithAgentMutation,
  useTriggerRunAiCheckMutation,
  useTriggerLocateExpensesActionMutation,
  useTriggerOperationsSummaryMutation,
  useRevertActionMutation,
  useSubmitActionFeedbackMutation,
  useSubmitOverallFeedbackMutation
} = daAssistantApi;

export type TriggerDaAssistantActionTrigger = ReturnType<
  typeof useTriggerDaAssistantActionMutation
>[0];

export type TriggerParameterFixTrigger = ReturnType<
  typeof useTriggerParameterFixMutation
>[0];

export type TriggerApproveExpensesTrigger = ReturnType<
  typeof useTriggerApproveExpensesMutation
>[0];

export type TriggerCaAllocateTrigger = ReturnType<
  typeof useTriggerCaAllocateMutation
>[0];

export type TriggerRunAiCheckTrigger = ReturnType<
  typeof useTriggerRunAiCheckMutation
>[0];

export type TriggerLocateExpensesActionTrigger = ReturnType<
  typeof useTriggerLocateExpensesActionMutation
>[0];

export type TriggerOperationsSummaryTrigger = ReturnType<
  typeof useTriggerOperationsSummaryMutation
>[0];

export const invalidateDaAssistantCapabilities = (evaluationId: number) =>
  daAssistantApi.util.invalidateTags(daAssistantEvaluationTags(evaluationId));
